import type { Mark, Node, NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Selection } from '@atlaskit/editor-prosemirror/state';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

export const getLinkMarkAtCursor = (
	state: EditorState,
	from: number,
):
	| {
			linkMark: Mark;
			parentNode: Node;
			url: string;
			/** Document range of the link mark (start..end). */
			markRange: { from: number; to: number };
	  }
	| undefined => {
	if (
		!expValEqualsNoExposure('platform_editor_rovobutton_smartlink_toolbar_exp', 'isEnabled', true)
	) {
		return undefined;
	}

	const $pos = state.doc.resolve(from);
	const child = $pos.parent.maybeChild($pos.index());
	const linkMark = child?.marks.find((m) => m.type === state.schema.marks.link);
	const url = typeof linkMark?.attrs?.href === 'string' ? linkMark.attrs.href : undefined;

	if (linkMark && url && child) {
		// Walk both directions from the cursor to find the contiguous run of
		// text nodes that share this exact link mark — that's the mark range.
		const parentStart = $pos.start();
		const childIndex = $pos.index();
		let runFrom = parentStart;
		for (let i = 0; i < childIndex; i++) {
			runFrom += $pos.parent.child(i).nodeSize;
		}
		let runTo = runFrom + child.nodeSize;
		for (let i = childIndex - 1; i >= 0; i--) {
			const sibling = $pos.parent.child(i);
			if (sibling.marks.some((m) => m.eq(linkMark))) {
				runFrom -= sibling.nodeSize;
			} else {
				break;
			}
		}
		for (let i = childIndex + 1; i < $pos.parent.childCount; i++) {
			const sibling = $pos.parent.child(i);
			if (sibling.marks.some((m) => m.eq(linkMark))) {
				runTo += sibling.nodeSize;
			} else {
				break;
			}
		}
		return {
			parentNode: $pos.parent,
			linkMark,
			url,
			markRange: { from: runFrom, to: runTo },
		};
	}

	return undefined;
};

export const getSupportedNodesForAIModal = ({ schema: { nodes } }: EditorState): Set<NodeType> => {
	if (
		expValEqualsNoExposure('platform_editor_rovobutton_smartlink_toolbar_exp', 'isEnabled', true)
	) {
		return new Set<NodeType>(
			[nodes.table, nodes.inlineCard, nodes.blockCard, nodes.embedCard].filter(Boolean),
		);
	} else {
		return new Set<NodeType>([nodes.table]);
	}
};

export const isCellSelection = (state: EditorState, selection?: Selection): boolean => {
	const { tableHeader, tableCell, tabeRow, table } = state.schema.nodes;
	const { $from } = selection || state.selection;
	const parent = $from.parent;
	return [tableHeader, tableCell, tabeRow, table].includes(parent.type);
};
